import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckSquare, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MagicLogin = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [error, setError] = useState('');
  const hasValidated = useRef(false);

  useEffect(() => {
    const validateMagicLink = async () => {
      // Prevent double execution in StrictMode
      if (hasValidated.current) return;
      hasValidated.current = true;
      
      try {
        const response = await fetch(`${API}/auth/magic-link/${token}`);
        const responseClone = response.clone();
        
        let data;
        try {
          data = await response.json();
        } catch (parseError) {
          const text = await responseClone.text();
          console.error('Failed to parse magic link response:', text);
          throw new Error('Invalid server response');
        }
        
        if (response.ok) {
          login(data.access_token, data.user);
          setStatus('success');
          toast.success(`Welcome, ${data.user.name}!`);
          
          // Check for redirect parameter (e.g., /tasks/123)
          const redirectPath = searchParams.get('redirect');
          const targetPath = redirectPath || '/';
          
          // Redirect after a short delay
          setTimeout(() => {
            navigate(targetPath);
          }, 1500);
        } else {
          setStatus('error');
          setError(data.detail || 'Invalid or expired magic link');
        }
      } catch (err) {
        console.error('Magic link error:', err);
        setStatus('error');
        setError('Failed to validate magic link. Please try again.');
      }
    };

    if (token) {
      validateMagicLink();
    } else {
      setStatus('error');
      setError('No magic link token provided');
    }
  }, [token, login, navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/logo-dark.png" 
              alt="2BeLive" 
              className="h-16 w-auto hidden dark:block"
            />
            <img 
              src="/logo-light.png" 
              alt="2BeLive" 
              className="h-16 w-auto dark:hidden"
            />
          </div>
          <CardTitle className="font-heading text-2xl">TaskFlow AI</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {status === 'loading' && (
            <div className="py-8">
              <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-primary" />
              <p className="text-muted-foreground">Validating your magic link...</p>
            </div>
          )}
          
          {status === 'success' && (
            <div className="py-8">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
              <p className="text-lg font-medium mb-2">Login Successful!</p>
              <p className="text-muted-foreground">Redirecting to dashboard...</p>
            </div>
          )}
          
          {status === 'error' && (
            <div className="py-8">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
              <p className="text-lg font-medium mb-2">Magic Link Invalid</p>
              <p className="text-muted-foreground mb-4">{error}</p>
              <button
                onClick={() => navigate('/auth')}
                className="text-blue-500 hover:text-blue-600 underline"
              >
                Go to login page
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MagicLogin;
